/**
 * Route guards.
 *
 * The legacy app enforced role separation purely visually — every screen for
 * every role was in the DOM, and login handlers hid the ones that did not apply.
 * Anyone who ran `showView('admin')` in the console got the admin screen. The
 * backend still checks authorisation, so this was never a data breach, but it
 * did expose UI that had no business being reachable. These guards make the
 * client-side boundary real.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ROLE_HOME, useAuth } from '../context/AuthContext';
import { Spinner } from '../components/common';

/** Blocks anything without a live session, remembering where the user was going. */
export function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return children ?? <Outlet />;
}

/**
 * Restricts a branch to a set of roles. A signed-in user with the wrong role is
 * sent to their own home rather than to the login screen.
 */
export function RequireRole({ roles = [], children }) {
  const { role, homePath } = useAuth();

  if (!role) return <Navigate to="/login" replace />;
  if (roles.length > 0 && !roles.includes(role)) {
    return <Navigate to={homePath} replace />;
  }

  return children ?? <Outlet />;
}

/** "/" resolves to the signed-in role's home, or to the login screen. */
export function LandingRedirect() {
  const { session, role } = useAuth();
  if (!session) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_HOME[role] || '/login'} replace />;
}

/**
 * Placeholder for suspended route chunks.
 *
 * A route swap replaces the whole page, so this takes the whole viewport —
 * a panel-shaped loader inside an otherwise empty shell reads as a broken page.
 */
export function RouteFallback() {
  return <Spinner variant="cover" />;
}
